import React, { useState, useEffect, useContext, useMemo, useCallback } from "react";
import "./Modules.css";
import ModuleWindow from "../ModuleWindow/ModuleWindow";
import * as BsIcons from "react-icons/bs";
import * as TbIcons from "react-icons/tb";
import * as MdIcons from "react-icons/md";
import * as Io5Icons from "react-icons/io5";
import * as RiIcons from "react-icons/ri";
import * as GrIcons from "react-icons/gr";
import * as F6Icons from "react-icons/fa6";
import { useLanguage } from "../../LanguageProvider";
import Projects from "../../pages/ProjectsWindow/Projects";
import ApiPostGet from "../../pages/ApiPostGetPage/ApiPostGet";
import { GeneralContext } from "../../GeneralProvider";
import Collaborators from "../../pages/CollaboratorsPage/Collaborators";
import Documentation from "../../pages/DocumentationPage/Documentation";
import TaskManagement from "../../pages/TaskManagementPage/TaskManagement";
import CustomPlan from "../../pages/CustomPlan/CustomPlan";
import Kronos from "../../pages/KronosPage/Kronos";
import { TbAddressBook } from "react-icons/tb";
import Profile from "../../pages/ProfilePage/Profile";
import Notebook from "../../pages/Notebook/Notebook";
import SocialHive from "../../pages/SocialHive/SocialHive";
import Games from "../../pages/Games/Games";
import Automation from "../../pages/Automation/Automation";
import Monitoring from "../../pages/Monitoring/Monitoring";
import FlowchartStudio from "../../pages/FlowchartStudio/FlowchartStudio";
import NovaPlayer from "../../pages/NovaPlayer/NovaPlayer";
import Achieve from "../../pages/Achieve/Achieve";
import Checklist from "../../pages/Checklist/Checklist";
import Lodrive from "../../pages/Lodrive/Lodrive";
import Events from "../../pages/Events/Events";

export default function Modules() {
  const { getTranslation } = useLanguage();
  const {
    selectedProjectID,
    collections,
    openedWindows,
    setOpenedWindows,
    handleUpdateOpenedWindows,
    getUserAuthorizations,
    projects,
    isSubscriptionExpired,
    shChannelUri,
    setShChannelUri,
    dmRoomUri,
    eventIDUri,
    subscriptionType
  } = useContext(GeneralContext);

  const [projectAuthorization, setProjectAuthorization] = useState(null);
  const [allFolders, setAllFolders] = useState([]);
  const [socialFolders, setSocialFolders] = useState([]);
  const [headerFolders, setHeaderFolders] = useState([]);

  const listAllFolders = useCallback(() => {
    return [...(allFolders || []), ...(socialFolders || []), ...(headerFolders || [])];
  }, [allFolders, socialFolders, headerFolders]);

  const resolveFolderByKeyOrName = useCallback((keyOrName) => {
    const all = listAllFolders();
    // Önce key ile ara
    let f = all.find(f => f.key === keyOrName);
    if (f) return f;
    // Geriye dönük: name ile ara (yenileme/legacy durumları)
    f = all.find(f => f.name === keyOrName);
    return f;
  }, [listAllFolders]);

  const openModal = useCallback((
    folderKeyOrName,
    checkData,
    opts = {}
  ) => {
    const folder = resolveFolderByKeyOrName(folderKeyOrName);
    if (!folder) return;
    const {
      name: displayName,
      module,
      icon,
      multiple = false,
      independent = false
    } = folder;


    let existingWindow = [];

    const source = checkData || openedWindows;

    if (independent) {
      existingWindow = source.find(win => (win.key === folder.key || win.name === displayName) && !multiple);
    } else {
      if (!selectedProjectID) {
        existingWindow = source.find((win) =>
          (win.key === folder.key || win.name === displayName) && !multiple
        );
      } else {
        existingWindow = source.find(
          (win) =>
            (win.key === folder.key || win.name === displayName) &&
            !multiple &&
            win.projectId === selectedProjectID
        );
      }
    }


    if (existingWindow && !multiple) {
      const updatedWindows = source.map((win) => ({
        ...win,
        isActive: win.id === existingWindow.id ? true : win.isActive,
        isSelected: win.id === existingWindow.id,
      }));

      setOpenedWindows(updatedWindows);
      handleUpdateOpenedWindows(updatedWindows);
      return;
    }


    const id = generateUniqueId();
    const offset = 20;
    const positionIndex = checkData ? checkData.length : openedWindows.length;
    const newPosition = {
      x: window.innerWidth / 2 - 650 + offset * positionIndex,
      y: window.innerHeight / 2 - 350 + offset * positionIndex,
    };
    const defaultSize = { width: 1300, height: 750 };

    const newWindowData = {
      key: folder.key,              // 🔑 sabit kimlik
      name: displayName,
      icon,
      position: newPosition,
      size: defaultSize,
      id,
      isActive: true,
      isSelected: true,
      projectId: selectedProjectID || "",
      independent,
    };

    let updatedWindows = [];

    if (checkData) {
      updatedWindows = [
        ...checkData.map((win) => ({ ...win, isSelected: false })),
        newWindowData,
      ];
    } else {
      updatedWindows = [
        ...openedWindows.map((win) => ({ ...win, isSelected: false })),
        newWindowData,
      ];
    }
    setOpenedWindows(updatedWindows);
    handleUpdateOpenedWindows(updatedWindows);
  }, [selectedProjectID, openedWindows, setOpenedWindows, handleUpdateOpenedWindows, resolveFolderByKeyOrName]);

  useEffect(() => {
    if (subscriptionType === "basic" || subscriptionType === "professional") {
      setAllFolders([
        {
          key: "collaborators",
          name: getTranslation("collaborators"),
          icon: "TbAddressBook",
          library: TbIcons,
          module: Collaborators,
          multiple: false,
          independent: true,
        },
        {
          key: "workspaceManager",
          name: getTranslation("workSpaceManager"),
          icon: "TbFolder",
          library: TbIcons,
          module: Projects,
          multiple: false,
        },
        {
          key: "apiPostGet",
          name: getTranslation("apiPostGet"),
          icon: "TbApi",
          library: TbIcons,
          module: ApiPostGet,
          multiple: false,
        },
        {
          key: "apiDocs",
          name: "API " + getTranslation("documentation"),
          icon: "TbNews",
          library: TbIcons,
          module: Documentation,
          multiple: false,
        },
        {
          key: "taskManagement",
          name: getTranslation("taskManagement"),
          icon: "BsCalendar2Range",
          library: BsIcons,
          module: TaskManagement,
          multiple: false,
        },
        {
          key: "kronos",
          name: "Kronos",
          icon: "TbHourglassLow",
          library: TbIcons,
          module: Kronos,
          multiple: false,
        },
        {
          key: "checklist",
          name: "Checklist",
          icon: "FaListCheck",
          library: F6Icons,
          module: Checklist,
          multiple: false,
        },
      ]);

      setSocialFolders([
        {
          key: "notebook",
          name: getTranslation("notebook"),
          icon: "TbNotebook",
          library: TbIcons,
          module: Notebook,
          multiple: false,
          independent: true,
        },
        {
          key: "automation",
          name: getTranslation("automation"),
          icon: "RiFlowChart",
          library: RiIcons,
          module: Automation,
          multiple: false,
          independent: true,
        },
        {
          key: "flowchart",
          name: "Flowchart",
          icon: "RiFlowChart",
          library: RiIcons,
          module: FlowchartStudio,
          multiple: false,
          independent: true,
        },
        {
          key: "achieve",
          name: "Achieve",
          icon: "MdOutlineHive",
          library: MdIcons,
          module: Achieve,
          multiple: false,
          independent: true,
        },
        {
          key: "socialHive",
          name: "Social Hive",
          icon: "MdOutlineHive",
          library: MdIcons,
          module: SocialHive,
          multiple: false,
          independent: true,
        },
        {
          key: "events",
          name: getTranslation("events"),
          icon: "TbTicket",
          library: TbIcons,
          module: Events,
          multiple: false,
          independent: true,
        },
        {
          key: "monitoring",
          name: "Monitoring",
          icon: "TbHeartRateMonitor",
          library: TbIcons,
          module: Monitoring,
          multiple: false,
          independent: true,
        },
        {
          key: "games",
          name: getTranslation("games"),
          icon: "IoGameControllerOutline",
          library: Io5Icons,
          module: Games,
          multiple: false,
          independent: true,
        },
        {
          key: "nova",
          name: "Nova",
          icon: "MdMusicNote",
          library: MdIcons,
          module: NovaPlayer,
          multiple: false,
          independent: true,
        },
        {
          key: "lodrive",
          name: "Lodrive",
          icon: "GrStorage",
          library: GrIcons,
          module: Lodrive,
          multiple: false,
          independent: true,
        },
        // {
        //   name: "Code Mechanic",
        //   icon: "HiOutlineWrenchScrewdriver",
        //   library: HiIcons,
        //   module: CMVotePage,
        //   multiple: false,
        //   independent: true,
        // },
        //   {
        //   name: "Automation",
        //   icon: "RiFlowChart",
        //   library: RiIcons,
        //   module: AutomationVotePage,
        //   multiple: false,
        //   independent: true,
        // },
        //   {
        //   name: "Code Boy",
        //   icon: "PiCowboyHatLight",
        //   library: PiIcons,
        //   module: CBVotePage,
        //   multiple: false,
        //   independent: true,
        // },
      ]);

      setHeaderFolders([
        {
          key: "subscription",
          name: getTranslation("subscription"),
          icon: "TbBolt",
          library: TbIcons,
          module: CustomPlan,
          multiple: false,
          independent: true,
        },
        {
          key: "profile",
          name: getTranslation("profile"),
          icon: "TbUser",
          library: TbIcons,
          module: Profile,
          multiple: false,
          independent: true,
        },
      ]);
    } else {
      setAllFolders([{
        key: "collaborators",
        name: getTranslation("collaborators"),
        icon: "TbAddressBook",
        library: TbIcons,
        module: Collaborators,
        multiple: false,
        independent: true,
      }]);
      setSocialFolders([
        {
          key: "notebook",
          name: getTranslation("notebook"),
          icon: "TbNotebook",
          library: TbIcons,
          module: Notebook,
          multiple: false,
          independent: true,
        },
        {
          key: "automation",
          name: getTranslation("automation"),
          icon: "RiFlowChart",
          library: RiIcons,
          module: Automation,
          multiple: false,
          independent: true,
        },
        {
          key: "flowchart",
          name: "Flowchart",
          icon: "RiFlowChart",
          library: RiIcons,
          module: FlowchartStudio,
          multiple: false,
          independent: true,
        },
        {
          key: "achieve",
          name: "Achieve",
          icon: "MdOutlineHive",
          library: MdIcons,
          module: Achieve,
          multiple: false,
          independent: true,
        },
        {
          key: "socialHive",
          name: "Social Hive",
          icon: "MdOutlineHive",
          library: MdIcons,
          module: SocialHive,
          multiple: false,
          independent: true,
        },
        {
          key: "events",
          name: getTranslation("events"),
          icon: "TbTicket",
          library: TbIcons,
          module: Events,
          multiple: false,
          independent: true,
        },
        {
          key: "monitoring",
          name: "Monitoring",
          icon: "TbHeartRateMonitor",
          library: TbIcons,
          module: Monitoring,
          multiple: false,
          independent: true,
        },
        {
          key: "games",
          name: getTranslation("games"),
          icon: "IoGameControllerOutline",
          library: Io5Icons,
          module: Games,
          multiple: false,
          independent: true,
        },
        {
          key: "nova",
          name: "Nova",
          icon: "MdMusicNote",
          library: MdIcons,
          module: NovaPlayer,
          multiple: false,
          independent: true,
        },
        {
          key: "lodrive",
          name: "Lodrive",
          icon: "GrStorage",
          library: GrIcons,
          module: Lodrive,
          multiple: false,
          independent: true,
        },
        // {
        //   name: "Code Mechanic",
        //   icon: "HiOutlineWrenchScrewdriver",
        //   library: HiIcons,
        //   module: CMVotePage,
        //   multiple: false,
        //   independent: true,
        // },
        //   {
        //   name: "Automation",
        //   icon: "RiFlowChart",
        //   library: RiIcons,
        //   module: AutomationVotePage,
        //   multiple: false,
        //   independent: true,
        // },
        //   {
        //   name: "Code Boy",
        //   icon: "PiCowboyHatLight",
        //   library: PiIcons,
        //   module: CBVotePage,
        //   multiple: false,
        //   independent: true,
        // },
      ]);
    }
  }, [isSubscriptionExpired, getTranslation, subscriptionType]);

  useEffect(() => {
    if (!shChannelUri && !dmRoomUri && !eventIDUri) return;
    const alreadyOpen = openedWindows.some(win => win.key === "socialHive" || win.name === "Social Hive");
    if (!alreadyOpen) {
      openModal("socialHive", null, { independent: true });
    }
  }, [shChannelUri, dmRoomUri, eventIDUri, openedWindows, openModal]);

  const fetchAuthorizations = async () => {
    const res = await getUserAuthorizations();

    if (res?.result?.length > 0) {
      const matchedProject = res?.result?.find(
        (item) => item.projectID === selectedProjectID
      );

      if (matchedProject) {
        setProjectAuthorization(matchedProject.authorizations);
        return matchedProject.authorizations;
      } else {
        setProjectAuthorization(null);
      }
    }
  };

  const folders = useMemo(() => {
    const hasValidApis = collections?.some(
      (collection) => (collection.groups || collection.ungroup) && (collection.groups.length > 0 || collection.ungroup.length > 0)
    );

    const filteredFolders = selectedProjectID
      ? allFolders.filter(
        (folder) =>
          folder.name !== getTranslation("documentation") || hasValidApis
      )
      : [
        {
          name: getTranslation("collaborators"),
          icon: "TbAddressBook",
          library: TbIcons,
          module: Collaborators,
          multiple: false,
          independent: true,
        },
      ];

    if (
      projectAuthorization?.type !== "partner" &&
      projectAuthorization?.type !== "owner"
    ) {
      const foldersWithoutProject = filteredFolders.filter(
        (folder) => folder.name !== getTranslation("workSpaceManager")
      );
      return foldersWithoutProject;
    }

    return filteredFolders;
  }, [selectedProjectID, collections, getTranslation, fetchAuthorizations]);

  useEffect(() => {
    fetchAuthorizations();
  }, [selectedProjectID]);

  const generateUniqueId = () => "_" + Math.random().toString(36).substr(2, 9);

  const closeModal = (id) => {
    setOpenedWindows((prev) => {
      const updatedWindows = prev.filter((win) => win.id !== id);
      handleUpdateOpenedWindows(updatedWindows);
      return updatedWindows;
    });
  };

  const updateWindowPosition = (id, newPosition, newSize = null) => {
    setOpenedWindows((prev) =>
      prev.map((win) =>
        win.id === id
          ? {
            ...win,
            position: newPosition,
            size: newSize || win.size,
          }
          : win
      )
    );
  };

  const createModuleWindow = (
    name,
    module,
    icon,
    position,
    id,
    isActive = true,
    isSelected = false,
    size = { width: 1000, height: 750 },
    projectId,
    independent = false
  ) => {
    if (
      !independent &&
      projectId !== selectedProjectID &&
      projects?.length !== 0
    )
      return null; // Yanlış proje ID'sine sahip pencereleri oluşturma

    return (
      <ModuleWindow
        key={id}
        projectId={projectId}
        name={name}
        icon={icon}
        children={module}
        currentPosition={position}
        currentSize={size}
        setOpenedWindows={setOpenedWindows}
        openedWindows={openedWindows}
        updateWindowPosition={updateWindowPosition}
        id={id}
        isActive={isActive}
        isSelected={isSelected}
        onClose={() => closeModal(id)}
        independent={independent}
      />
    );
  };

  // const windowsWithModules = useMemo(() => {
  //   return openedWindows.map((win) => {
  //     let folder = folders.find((f) => f.name === win.name);

  //     if (!folder) {
  //       folder = socialFolders.find((f) => f.name === win.name);
  //     }

  //     return {
  //       ...win,
  //       module: folder?.module,
  //       icon: folder?.icon,
  //       independent: win?.independent,
  //     };
  //   });
  // }, [openedWindows, folders, socialFolders]);

  const windowsWithModules = useMemo(() => {
    const all = [...folders, ...socialFolders, ...headerFolders];
    return openedWindows.map((win) => {
      // Önce key ile, sonra name ile bul
      let folder =
        all.find((f) => f.key && f.key === win.key) ||
        all.find((f) => f.name === win.name);
      return {
        ...win,
        // Görünen adı güncel tut (çeviri değişmiş olabilir)
        name: folder?.name ?? win.name,
        module: folder ? React.createElement(folder.module, { openModal }) : win.module,
        icon: folder ? folder.icon : win.icon,
        independent: win.independent,
      };
    });
  }, [openedWindows, folders, socialFolders, headerFolders, openModal]);

  return (
    <>
      {windowsWithModules
        .filter((win) => win.isActive)
        .map((win) =>
          createModuleWindow(
            win.name,
            win.module,
            win.icon,
            win.position,
            win.id,
            win.isActive,
            win.isSelected,
            win.size,
            win.projectId,
            win.independent
          )
        )}
      {isSubscriptionExpired && <div style={{ height: '100vh', width: '100vh' }}></div>}

      <div className="module-folder">
        {selectedProjectID ? (
          <>
            {" "}
            {folders.map((folder, index) => {
              const IconComponent = folder.library[folder.icon];
              return (
                <div
                  key={index}
                  className="folder-item"
                  onClick={() => openModal(folder.key)}
                >
                  <div className="folder-icon">
                    <IconComponent size={24} />
                  </div>
                  <span className="folder-name">{folder.name}</span>
                </div>
              );
            })}
          </>
        ) : (
          <>
            <div
              className="folder-item"
              onClick={() => openModal("collaborators", null, { independent: true })}
            >
              <div className="folder-icon">
                <TbAddressBook size={24} />
              </div>
              <span className="folder-name">
                {getTranslation("collaborators")}
              </span>
            </div>
          </>
        )}
      </div>
      <div className="module-social-folder">
        {socialFolders.map((folder, index) => {
          const IconComponent = folder.library[folder.icon];
          return (
            <div
              key={index}
              className="folder-item"
              onClick={() => openModal(folder.key)}
            >
              <div
                style={{ backgroundColor: (folder.name === "Code Mechanic" || folder.name === "Automation" || folder.name === "Code Boy") && '#ccc', color: (folder.name === "Code Mechanic" || folder.name === "Automation" || folder.name === "Code Boy") && '#fff' }}
                className="folder-icon">
                <IconComponent size={24} />
              </div>
              <span className="folder-name" style={{ color: (folder.name === "Code Mechanic" || folder.name === "Automation" || folder.name === "Code Boy") && '#ccc' }}>{folder.name}</span>
            </div>
          );
        })}
      </div>
    </>
  );
}
